import env from 'env';
import { ObjectValues } from 'types';
import mixpanel from 'mixpanel-browser';
import {
  LogoutProps,
  ErrorViewedProps,
  PageViewedProps,
  DocumentEventProps,
  MixpanelEventProps,
  SearchInitiatedEventProps,
  NotificationViewedInitiatedEventProps,
  SecureLinkCreationInitiatedEventProps,
  LeadStatusUpdateInitiatedEventProps,
  ListingWatchedInitiatedEventProps,
  ListingVisibilityChangeInitiatedEventProps,
  ListingCreationInitiatedEventProps,
  MobileVerificationInitiatedEventProps,
  EnquiryCreationInitiatedEventProps,
  EnquiryWithdrawalInitiatedEventProps,
  SignupInitiatedProps,
  DealCreationInitiatedEventProps,
  SoftSignalInitiatedEventProps,
  QuickFileReleaseInitiatedEventProps,
  DocumentPublishedEventProps,
  QuestionSubmissionInitiatedEventProps,
  QuestionReplyInitiatedEventProps,
  AnswerReviewInitiatedEventProps,
  QuestionReviewInitiatedEventProps,
} from 'models/MixpanelEvents';

const MIXPANEL_EVENT = {
  LOG_OUT: 'Log Out',
  ERROR_VIEWED: 'Error Viewed',
  PAGE_VIEWED: 'Page Viewed',
  DOCUMENT_VIEWED: 'Document Viewed',
  SEARCH_INITIATED: 'Search Initiated',
  NOTIFICATION_VIEWED_INITIATED: 'Notification Viewed Initiated',
  SECURE_LINK_CREATION_INITIATED: 'Secure Link Creation Initiated',
  LEAD_STATUS_UPDATE_INITIATED: 'Lead Status Update Initiated',
  LISTING_WATCHED_INITIATED: 'Listing Watched Initiated',
  LISTING_VISIBILITY_CHANGE_INITIATED: 'Listing Visibility Change Initiated',
  LISTING_CREATION_INITIATED: 'Listing Creation Initiated',
  MOBILE_VERIFICATION_INITIATED: 'Mobile Verification Initiated',
  ENQUIRY_CREATION_INITIATED: 'Enquiry Creation Initiated',
  ENQUIRY_WITHDRAWAL_INITIATED: 'Enquiry Withdrawal Initiated',
  SIGNUP_INITIATED: 'Signup Initiated',
  DEAL_CREATION_INITIATED: 'Deal Creation Initiated',
  SOFT_SIGNAL_INITIATED: 'Soft Signal Initiated',
  QUICK_FILE_RELEASE_INITIATED: 'Quick File Release Initiated',
  DOCUMENT_PUBLISHED: 'Document Published Initiated',
  QUESTION_SUBMISSION_INITIATED: 'Question Submission Initiated',
  QUESTION_REPLY_INITIATED: 'Question Reply Initiated',
  ANSWER_REVIEW_INITIATED: 'Answer Review Initiated',
  QUESTION_REVIEW_INITIATED: 'Question Review Initiated',
} as const;

type MixpanelEvents = ObjectValues<typeof MIXPANEL_EVENT>;

export default {
  isInitialized: false,

  init() {
    if (env.MIXPANEL_TOKEN) {
      mixpanel.init(env.MIXPANEL_TOKEN, {
        record_sessions_percent: env.IS_PROD ? 100 : 0, // records 80% of all sessions on prod env
      });
      this.isInitialized = true;
    }
  },

  identify(userId: string) {
    if (this.isInitialized) {
      mixpanel.identify(userId);
    }
  },

  reset() {
    if (this.isInitialized) {
      mixpanel.reset();
    }
  },

  trackEvent(eventName: MixpanelEvents, eventProps?: MixpanelEventProps) {
    if (this.isInitialized) {
      try {
        mixpanel.track(eventName, eventProps);
      } catch {
        return;
      }
    }
  },

  logout(props: LogoutProps) {
    this.trackEvent(MIXPANEL_EVENT.LOG_OUT, props);
  },

  errorViewed(props: ErrorViewedProps) {
    this.trackEvent(MIXPANEL_EVENT.ERROR_VIEWED, props);
  },

  pageViewed(props: PageViewedProps) {
    this.trackEvent(MIXPANEL_EVENT.PAGE_VIEWED, props);
  },

  documentViewed(props: DocumentEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DOCUMENT_VIEWED, props);
  },

  searchInitiated(props: SearchInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.SEARCH_INITIATED, props);
  },

  notificationViewedInitiated(props: NotificationViewedInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.NOTIFICATION_VIEWED_INITIATED, props);
  },

  secureLinkCreationInitiated(props: SecureLinkCreationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.SECURE_LINK_CREATION_INITIATED, props);
  },

  leadStatusUpdateInitiated(props: LeadStatusUpdateInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.LEAD_STATUS_UPDATE_INITIATED, props);
  },

  listingWathchedInitiated(props: ListingWatchedInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.LISTING_WATCHED_INITIATED, props);
  },

  listingVisibilityChangeInitiated(
    props: ListingVisibilityChangeInitiatedEventProps,
  ) {
    this.trackEvent(MIXPANEL_EVENT.LISTING_VISIBILITY_CHANGE_INITIATED, props);
  },

  listingCreationInitiated(props: ListingCreationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.LISTING_CREATION_INITIATED, props);
  },

  mobileVerificationInitiated(props: MobileVerificationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.MOBILE_VERIFICATION_INITIATED, props);
  },

  enquiryCreationInitiated(props: EnquiryCreationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.ENQUIRY_CREATION_INITIATED, props);
  },

  enquiryWithdrawalInitiated(props: EnquiryWithdrawalInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.ENQUIRY_WITHDRAWAL_INITIATED, props);
  },

  signupInitiated(props: SignupInitiatedProps) {
    this.trackEvent(MIXPANEL_EVENT.SIGNUP_INITIATED, props);
  },

  dealRoomCreationInitiated(props: DealCreationInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DEAL_CREATION_INITIATED, props);
  },

  softSignalInitiated(props: SoftSignalInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.SOFT_SIGNAL_INITIATED, props);
  },

  quickFileReleaseInitiated(props: QuickFileReleaseInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.QUICK_FILE_RELEASE_INITIATED, props);
  },

  documentPublished(props: DocumentPublishedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.DOCUMENT_PUBLISHED, props);
  },

  questionSubmissionInitiated(props: QuestionSubmissionInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.QUESTION_SUBMISSION_INITIATED, props);
  },

  questionReplyInitiated(props: QuestionReplyInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.QUESTION_REPLY_INITIATED, props);
  },

  answerReviewInitiated(props: AnswerReviewInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.ANSWER_REVIEW_INITIATED, props);
  },

  questionReviewInitiated(props: QuestionReviewInitiatedEventProps) {
    this.trackEvent(MIXPANEL_EVENT.QUESTION_REVIEW_INITIATED, props);
  },
};
